import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Idle } from "@ng-idle/core";
import { BehaviorSubject, fromEvent, Observable, Subscription } from "rxjs";
import {
  CaptchaValidationModel,
  GeneralResModel,
  LoginModel,
  RecaptchaRequestModel,
  RecaptchaResponseModel,
  TwoFABackupCodeModel,
  TwoFaVerifyModel,
} from "src/app/_models";
import { CommonResponse } from "src/app/_models/common-response.modle";
import {
  PwdValidatorParam,
  PwdValidatorRes,
} from "src/app/_models/password-policy/pwdvalidator-res.modal";
import { User } from "src/app/_models/user-models/user";
import { ApiService } from "../api/api.service";
import { AlertifyService } from "../common/alertify.service";
import { LocalStorageService } from "../storage/local-storage.service";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private apiPATH = "Auth/";

  client: User = new User();
  private idleStateSubscription = new Subscription();
  private userActivitySubscription = new Subscription();
  private idleTime = new BehaviorSubject<number>(0);
  idleTime$: Observable<number> = this.idleTime.asObservable();
  setedIdleTime: number | null;
  private activeRequests = 0;

  constructor(
    private idle: Idle,
    private api: ApiService,
    private _router: Router,
    private _alertifyService: AlertifyService
  ) {
    this.setupIdleService();
  }

  login(requst: LoginModel) {
    return this.api.post<CommonResponse>(`${this.apiPATH}login`, requst);
  }

  isDuplicateUserCheck(requst: LoginModel) {
    return this.api.post<CommonResponse>(
      `${this.apiPATH}IsDuplicateUserCheck`,
      requst
    );
  }

  userDuplicateCheck(userId: number) {
    return this.api.get<CommonResponse>(
      `${this.apiPATH}UserDuplicateCheck/${userId}`
    );
  }

  getSharebleLinkById(linkId: string) {
    return this.api.get<string>(`${this.apiPATH}getSharebleLinkById/${linkId}`);
  }

  LoginWithLine(code: string, state: string) {
    return this.api.get<any>(`${this.apiPATH}OAuth/${code}/${state}`);
  }

  getAccessToken(): string {
    return localStorage.getItem("token");
  }

  ValidatePassword(request: PwdValidatorParam) {
    return this.api.post<GeneralResModel<PwdValidatorRes>>(
      `${this.apiPATH}ValidatePassword`,
      request
    );
  }

  GetSecrateKey(orgId: number) {
    return this.api.get<CommonResponse>(
      `${this.apiPATH}GetSecrateKey/${orgId}`
    );
  }

  getUser2faAuthTotpSecretKey(userId: number) {
    return this.api.get<User>(
      `${this.apiPATH}GetUser2faAuthTotpSecretKey/${userId}`
    );
  }

  user2faAuth(request: User) {
    return this.api.post<boolean>(`${this.apiPATH}User2faAuth`, request);
  }

  get2faAuthBackupCodes(user: User) {
    return this.api.post<TwoFABackupCodeModel[]>(
      `${this.apiPATH}Get2faAuthBackupCodes`,
      user
    );
  }

  user2faVerifyingTOTPBySecretkey(request: TwoFaVerifyModel) {
    return this.api.post<boolean>(
      `${this.apiPATH}User2faVerifyingTOTPBySecretkey`,
      request
    );
  }

  getUser2faAuthVerifyingTOTP(request: User) {
    return this.api.post<boolean>(
      `${this.apiPATH}GetUser2faAuthVerifyingTOTP`,
      request
    );
  }

  user2faAuthBackupCodesVerify(request: User) {
    return this.api.post<GeneralResModel<User>>(
      `${this.apiPATH}User2faAuthBackupCodesVerify`,
      request
    );
  }

  user2faAuthDisable(empId: number) {
    return this.api.post<GeneralResModel<string>>(
      `${this.apiPATH}User2faAuthDisable`,
      empId
    );
  }

  // CAPTCHA
  refreshCaptcha() {
    return this.api.get<string>(`${this.apiPATH}captcha-image`);
  }

  captchaValidate(captchaResponse: CaptchaValidationModel) {
    return this.api.post<CommonResponse>(
      `${this.apiPATH}captcha-validate`,
      captchaResponse
    );
  }

  verifyRecaptcha(request: RecaptchaRequestModel) {
    return this.api.post<GeneralResModel<RecaptchaResponseModel>>(
      `${this.apiPATH}verify-recaptcha`,
      request
    );
  }

  // IDLE TIME Functionality START
  ngOnDestroy() {
    this.cleanupSubscriptions();
    this.idle.stop();
  }

  updateIdleTime(time: number): void {
    const idleTime = time - 5;

    if (idleTime <= 0) {
      this.idle.setIdle(1); // minimum positive value
    } else {
      this.idle.setIdle(idleTime);
    }

    this.idle.setTimeout(5); // timeout period after idle
    this.idle.watch();
  }

  setupIdleService() {
    this.cleanupSubscriptions();
    this.client = LocalStorageService.getUserStorageValue();
    this.setedIdleTime =
      this.idleTime.value ||
      (this.client?.IdleTime ? this.client.IdleTime : 600);

    // Configure idle timeout
    this.idle.setIdle(this.setedIdleTime - 5); // e.g. 175 seconds idle period
    this.idle.setTimeout(5); // 5 seconds countdown after idle before timeout

    // Subscribe to idle events and add to idleStateSubscription
    this.idleStateSubscription.add(
      this.idle.onIdleEnd.subscribe(() => {
        console.log("Idle ended - user activity detected, timer reset.");
      })
    );

    this.idleStateSubscription.add(
      this.idle.onIdleStart.subscribe(() => {
        console.log("User has gone idle.");
      })
    );

    this.idleStateSubscription.add(
      this.idle.onTimeoutWarning.subscribe((countdown: number) => {
        console.log(`Timeout warning - will logout in ${countdown} seconds.`);
      })
    );

    this.idleStateSubscription.add(
      this.idle.onTimeout.subscribe(() => {
        console.log("Idle timeout reached. Logging out.");
        this.autoLogout();
      })
    );

    // Listen for user activity globally to reset idle timer
    this.userActivitySubscription = fromEvent(document, "mousemove").subscribe(
      () => {
        this.idle.watch();
      }
    );

    // Start watching idle time
    this.idle.watch();
    // console.log('Idle watch started with idle time:', this.setedIdleTime);
  }

  resetIdleService() {
    this.idle.stop();
    this.idle.watch();
    this.cleanupSubscriptions();
    this.setupIdleService();
  }

  pauseIdleService() {
    this.activeRequests++;
    if (this.activeRequests === 1) {
      this.idle.stop();
      // console.log('Idle watch paused due to active request.');
    }
  }

  resumeIdleService() {
    this.activeRequests--;
    // console.log('Active requests count:', this.activeRequests);
    if (this.activeRequests <= 0) {
      this.activeRequests = 0;
      this.idle.watch();
      // console.log('Idle watch resumed after requests completed.');
    }
  }

  autoLogout() {
    return this.api
      .get<any>(`${this.apiPATH}logout/${this.client.EmpId}/S`) // "S" for Session Timeout.
      .subscribe(() => {
        LocalStorageService.removeAllStorageValue();
        this.cleanupSubscriptions();
        this.idle.stop();
        this._router.navigate(["/login"]);
        this._alertifyService.success("Session timed out due to inactivity.");
      });
  }

  logout(userId: number) {
    this.cleanupSubscriptions();
    this.idle.stop();
    return this.api.get<any>(`${this.apiPATH}logout/${userId}`);
  }

  private cleanupSubscriptions() {
    this.idleStateSubscription.unsubscribe();
    this.idleStateSubscription = new Subscription();

    if (this.userActivitySubscription) {
      this.userActivitySubscription.unsubscribe();
    }
  }
  // IDLE TIME Functionality END
}
